import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import qs from 'qs';
import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { Organization } from 'dashboard/models/organization';
import { MfaMethod, User } from 'dashboard/models/user';
import ConfirmMfaPhoneModal from 'dashboard/views/DashboardUserProfileView/ConfirmMfaPhoneModal';
import ConfirmMfaTotpModal from 'dashboard/views/DashboardUserProfileView/ConfirmMfaTotpModal';
import RotateRecoveryCodeModal from 'dashboard/views/DashboardUserProfileView/RotateRecoveryCodeModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useToggle from 'lib/hooks/useToggle';

const HelpMessage = styled.span`
  /* override global anchor styles */
  > * {
    font-size: inherit;
    font-weight: inherit;
  }
`;

interface MultiFactorAuthenticationProps {
  currentUser: User;
  organization: Organization;
  setupMfaEmail: () => Promise<void>;
  enableMfaEmail: (token: string) => Promise<void>;
  disableMfaEmail: () => Promise<void>;
  mfaEmailLoading: boolean;
  setupMfaPhone: () => Promise<void>;
  enableMfaPhone: (code: string) => Promise<void>;
  disableMfaPhone: () => Promise<void>;
  mfaPhoneLoading: boolean;
  setupMfaTotp: () => Promise<void>;
  enableMfaTotp: (token: string) => Promise<void>;
  disableMfaTotp: () => Promise<void>;
  mfaTotpLoading: boolean;
  recoveryCode: string | undefined;
  rotateRecoveryCode: () => Promise<void>;
  rotateRecoveryCodeLoading: boolean;
}

const MultiFactorAuthentication = ({
  currentUser,
  organization,
  setupMfaEmail,
  enableMfaEmail,
  disableMfaEmail,
  mfaEmailLoading,
  setupMfaPhone,
  enableMfaPhone,
  disableMfaPhone,
  mfaPhoneLoading,
  setupMfaTotp,
  enableMfaTotp,
  disableMfaTotp,
  recoveryCode,
  rotateRecoveryCode,
  rotateRecoveryCodeLoading,
}: MultiFactorAuthenticationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmation_token: confirmationToken } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const { 'enabled-mfa-methods': organizationEnabledMfaMethods } = organization.attributes;

  const {
    'enabled-mfa-methods': enabledMfaMethods,
    'available-mfa-methods': availableMfaMethods,
    'mfa-email-confirmation-token-expires-at': mfaEmailConfirmationTokenExpiresAt,
    'mfa-phone-confirmation-code-expires-at': mfaPhoneConfirmationCodeExpiresAt,
    'totp-provisioning-uri': totpProvisioningUri,
    'phone-number': phoneNumber,
  } = currentUser.attributes;

  const organizationEnabledMfaMethodsSet = new Set(organizationEnabledMfaMethods);
  const enabledMfaMethodsSet = new Set(enabledMfaMethods);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const showRecoveryCode = availableMfaMethods.length > 0;

  const [
    enableMfaPhoneModalOpen,
    { enable: showEnableMfaPhoneModal, disable: hideEnableMfaPhoneModal },
  ] = useToggle(false);
  const [
    enableMfaTotpModalOpen,
    { enable: showEnableMfaTotpModal, disable: hideEnableMfaTotpModal },
  ] = useToggle(false);
  const [
    rotateRecoveryCodeModalOpen,
    { enable: showRotateRecoveryCodeModal, disable: hideRotateRecoveryCodeModal },
  ] = useToggle(false);

  useEffect(() => {
    if (confirmationToken != null && typeof confirmationToken === 'string') {
      navigate(location.pathname);
      enableMfaEmail(confirmationToken);
    }
  }, []);

  if (organizationEnabledMfaMethodsSet.size === 0) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title>2-factor authentication</Card.Title>
        <Paragraph>
          Add an extra layer of security to your account by setting up 2-factor authentication.
        </Paragraph>
        <Paragraph>
          When signing in, you will be prompted to provide one of the authentication methods you
          have enabled below.
        </Paragraph>
        <Spacer height={3} />
        <TableLayout
          dataTest='multi-factor-authentication-table'
          headerTR={
            <>
              <TableTH>2-factor methods</TableTH>
              <TableTH>{/*Description*/}</TableTH>
              <TableTH>{/*Status*/}</TableTH>
            </>
          }
          nullState={null}
        >
          <MultiFactorAuthenticationRow
            id='multi-factor-authentication-table-row__totp'
            title='One-time password'
            description={
              <span>
                Use an application on your phone to get authentication codes when you sign in.
                <HelpIcon>
                  <HelpMessage>
                    We recommend using an application such as{' '}
                    <a href='https://authy.com/' target='_blank' rel='noreferrer'>
                      Authy
                    </a>
                    ,{' '}
                    <a href='https://1password.com/' target='_blank' rel='noreferrer'>
                      1Password
                    </a>
                    , or{' '}
                    <a href='https://lastpass.com/auth/' target='_blank' rel='noreferrer'>
                      LastPass Authenticator
                    </a>
                    . These applications support secure backup of your authentication codes in the
                    cloud and can be restored if you lose access to your device.
                  </HelpMessage>
                </HelpIcon>
              </span>
            }
            enabled={enabledMfaMethodsSet.has(MfaMethod.Totp)}
            onEnable={setupMfaTotp}
            onDisable={disableMfaTotp}
            pending={!enabledMfaMethodsSet.has(MfaMethod.Totp) && totpProvisioningUri != null}
            hidden={!organizationEnabledMfaMethodsSet.has(MfaMethod.Totp)}
          >
            <ModalWrapper isOpen={enableMfaTotpModalOpen} handleCloseModal={hideEnableMfaTotpModal}>
              <ConfirmMfaTotpModal
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                provisioningUri={totpProvisioningUri}
                onClose={hideEnableMfaTotpModal}
                onSubmit={enableMfaTotp}
              />
            </ModalWrapper>
            <Button
              id='multi-factor-authentication__totp-btn--view-qr'
              data-test='multi-factor-authentication__totp-btn--view-qr'
              variant='basic'
              onClick={showEnableMfaTotpModal}
            >
              Enter code
            </Button>
          </MultiFactorAuthenticationRow>
          <MultiFactorAuthenticationRow
            id='multi-factor-authentication-table-row__email'
            title='Email'
            description="We'll send you an email with a confirmation link when you sign in."
            enabled={enabledMfaMethodsSet.has(MfaMethod.Email)}
            onEnable={setupMfaEmail}
            onDisable={disableMfaEmail}
            pending={
              mfaEmailConfirmationTokenExpiresAt != null &&
              DateTime.local() < DateTime.fromISO(mfaEmailConfirmationTokenExpiresAt)
            }
            hidden={!organizationEnabledMfaMethodsSet.has(MfaMethod.Email)}
          >
            <Button
              id='multi-factor-authentication__email-btn--resend'
              data-test='multi-factor-authentication__email-btn--resend'
              variant='basic'
              onClick={setupMfaEmail}
              disabled={mfaEmailLoading}
            >
              Resend
            </Button>
          </MultiFactorAuthenticationRow>
          <MultiFactorAuthenticationRow
            id='multi-factor-authentication-table-row__phone'
            title='Phone'
            description="We'll send you an SMS with a confirmation code when you sign in."
            enabled={enabledMfaMethodsSet.has(MfaMethod.Phone)}
            onEnable={setupMfaPhone}
            onDisable={disableMfaPhone}
            pending={
              mfaPhoneConfirmationCodeExpiresAt != null &&
              DateTime.local() < DateTime.fromISO(mfaPhoneConfirmationCodeExpiresAt)
            }
            hidden={
              !organizationEnabledMfaMethodsSet.has(MfaMethod.Phone) ||
              // TODO: move phone setup into setup modal and remove this restriction.
              phoneNumber == null ||
              phoneNumber === ''
            }
          >
            <ModalWrapper
              isOpen={enableMfaPhoneModalOpen}
              handleCloseModal={hideEnableMfaPhoneModal}
            >
              <ConfirmMfaPhoneModal
                onClose={hideEnableMfaPhoneModal}
                onSubmit={enableMfaPhone}
                onResend={setupMfaPhone}
                resendLoading={mfaPhoneLoading}
              />
            </ModalWrapper>
            <Button
              id='multi-factor-authentication__phone-btn--confirm'
              data-test='multi-factor-authentication__phone-btn--confirm'
              variant='basic'
              onClick={showEnableMfaPhoneModal}
              disabled={mfaPhoneLoading}
            >
              Enter code
            </Button>
          </MultiFactorAuthenticationRow>
          {showRecoveryCode && (
            <TableTR
              id='multi-factor-authentication-table-row__recovery-code'
              data-test='multi-factor-authentication-table-row__recovery-code'
            >
              <TableTD>Recovery Code</TableTD>
              <TableTD>
                Create a single-use code that will let you access your account in case you lose your
                2-factor device. Generating a recovery code will invalidate any existing codes.
              </TableTD>
              <TableTD>
                <ModalWrapper
                  isOpen={rotateRecoveryCodeModalOpen}
                  handleCloseModal={hideRotateRecoveryCodeModal}
                >
                  <RotateRecoveryCodeModal
                    recoveryCode={recoveryCode}
                    onClose={hideRotateRecoveryCodeModal}
                  />
                </ModalWrapper>
                <CellContents>
                  <Button
                    id='multi-factor-authentication__recovery-code-btn--confirm'
                    data-test='multi-factor-authentication__recovery-code-btn--confirm'
                    variant='basic'
                    width='auto'
                    onClick={async () => {
                      await rotateRecoveryCode();
                      showRotateRecoveryCodeModal();
                    }}
                    isPending={rotateRecoveryCodeLoading}
                    disabled={rotateRecoveryCodeLoading}
                  >
                    Generate
                  </Button>
                </CellContents>
              </TableTD>
            </TableTR>
          )}
        </TableLayout>
      </Card.Body>
    </Card>
  );
};

export default MultiFactorAuthentication;

const CellContents = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StatusCellContents = styled(CellContents)`
  justify-content: space-between;
`;

// We still want widths to be preserved
const PendingContainer = styled.div<{ pending: boolean }>`
  margin-left: ${Spacing[4]};
  visibility: ${(p) => (p.pending ? 'visible' : 'hidden')};
`;

interface MultiFactorAuthenticationRowProps {
  id: string;
  children: ReactNode;
  description: ReactNode;
  title: string;
  enabled: boolean;
  onEnable: () => Promise<void>;
  onDisable: () => Promise<void>;
  pending: boolean;
  hidden: boolean;
}

const MultiFactorAuthenticationRow = ({
  id,
  children,
  title,
  description,
  enabled,
  onEnable,
  onDisable,
  pending,
  hidden,
}: MultiFactorAuthenticationRowProps) => {
  const [disableModalOpen, { enable: showDisableModal, disable: hideDisableModal }] =
    useToggle(false);

  if (hidden) {
    return null;
  }

  return (
    <>
      <ModalWrapper isOpen={disableModalOpen} handleCloseModal={hideDisableModal}>
        <ConfirmationModal
          cancelAction={hideDisableModal}
          confirmAction={onDisable}
          header={
            pending ? 'Cancel 2-factor authentication setup?' : 'Disable 2-factor authentication?'
          }
          confirmActionText={pending ? 'Cancel' : 'Disable'}
          cancelActionText={pending ? 'Back' : 'Cancel'}
        >
          We encourage you to keep 2-factor authentication activated in order to add an extra layer
          of security to your account.
        </ConfirmationModal>
      </ModalWrapper>
      <TableTR id={id} data-test={id}>
        <TableTD>{title}</TableTD>
        <TableTD>{description}</TableTD>
        <TableTD>
          <StatusCellContents>
            <ToggleSlider
              data-test={`${id}-toggle`}
              onChange={enabled || pending ? showDisableModal : onEnable}
              value={enabled || pending}
              titleOn={pending ? 'Pending' : 'Enabled'}
              labelOn={pending ? 'Pending' : 'Enabled'}
              titleOff='Disabled'
              labelOff='Disabled'
            />
            <PendingContainer pending={pending}>{children}</PendingContainer>
          </StatusCellContents>
        </TableTD>
      </TableTR>
    </>
  );
};
